import { html } from 'lit';
import { MobxLitElement } from '@adobe/lit-mobx';
import { customElement, state } from 'lit/decorators.js';
import '@digital-realty/ix-icon-button/ix-icon-button.js';
import '@digital-realty/ix-date/ix-date.js';
import '@digital-realty/ix-divider/ix-divider.js';
import '@digital-realty/ix-icon';
import '@material/web/icon/icon.js';
import { format } from 'date-fns/format.js';
import { TWStyles } from '../../tw.js';
import { NotificationsState } from '../../state/NotificationState.js';
import { NotificationsStyle } from '../../styles/notifications-style.js';
import { DefaultDateFormat } from '../../constants/notifications.js';
import { FilterDialogEventDetail } from '../../constants/notification-types.js';
import { isNullOrUndefinedOrEmpty } from '../../helper/common.js';

@customElement('date-filters')
export class DateFilters extends MobxLitElement {
  static get styles() {
    return [TWStyles, NotificationsStyle];
  }

  @state() private fromDate? = !isNullOrUndefinedOrEmpty(
    NotificationsState.dateFilters.FROM_DATE
  )
    ? format(NotificationsState.dateFilters.FROM_DATE!, DefaultDateFormat)
    : undefined;

  @state() private toDate? = !isNullOrUndefinedOrEmpty(
    NotificationsState.dateFilters.TO_DATE
  )
    ? format(NotificationsState.dateFilters.TO_DATE!, DefaultDateFormat)
    : undefined;

  @state() private maxDate = format(new Date(), DefaultDateFormat);

  @state() private fromDateErrorText? = '';

  @state() private toDateErrorText? = '';

  onFromDateChange(fromDate: string) {
    // handling of clear date event of calendar
    if (fromDate === null || fromDate === '') this.fromDate = undefined;
    else {
      this.fromDate = fromDate;
      // clear error message
      if (this.fromDate !== undefined) this.fromDateErrorText = undefined;
    }
  }

  onToDateChange(toDate: string) {
    // handling of clear date event of calendar
    if (toDate === null || toDate === '') this.toDate = undefined;
    else {
      this.toDate = toDate;
      // clear error message
      if (this.toDate !== undefined) this.toDateErrorText = undefined;
    }
  }

  onApplyDateFilter(selectedvalue: Boolean) {
    if (selectedvalue) {
      if (this.toDate === undefined && this.fromDate !== undefined) {
        this.toDateErrorText = 'Please select to date';
        return;
      }

      if (this.fromDate === undefined && this.toDate !== undefined) {
        this.fromDateErrorText = 'Please select from date';
        return;
      }

      if (this.fromDate! > this.toDate!) {
        this.fromDateErrorText = 'From date cannot be later than To date.';
        this.fromDate = undefined;
        return;
      }

      this.fromDateErrorText = undefined;

      if (this.toDate! < this.fromDate!) {
        this.toDateErrorText = 'To date cannot be earlier than From date.';
        this.toDate = undefined;
        return;
      }

      this.toDateErrorText = undefined;
    }

    const datesChanged =
      selectedvalue &&
      this.toDateErrorText === undefined &&
      this.fromDateErrorText === undefined;

    if (datesChanged) {
      NotificationsState.setNotificationDateFilter(this.fromDate, this.toDate);
      this.requestUpdate();
    }

    const event = new CustomEvent<FilterDialogEventDetail>('on-selection', {
      detail: { returnValue: datesChanged },
      bubbles: true,
      composed: true,
    });

    this.dispatchEvent(event);
  }

  clearDateFilters() {
    this.fromDate = undefined;
    this.toDate = undefined;
    this.fromDateErrorText = undefined;
    this.toDateErrorText = undefined;
  }

  /* eslint-disable lit-a11y/click-events-have-key-events */
  render() {
    return html` <div class="flex flex-col p-4">
      <div class="flex flex-row items-center">
        <div class="grow items-center text-xl font-bold">Filter by Date</div>
        <div class="flex flex-row items-center" @click=${this.clearDateFilters}>
          <label class="grow text-sm text-right pr-1 cursor-pointer"
            >Clear Filter</label
          >
          <ix-icon-button
            data-testid="notifications-clear-date-filter"
            small
            appearance="default"
            icon="domain_verification_off"
            class="active-icon"
          ></ix-icon-button>
        </div>
      </div>
      <ix-divider class="my-2"></ix-divider>
      <div
        class="flex xl:flex-row lg:flex-col md:flex-col sm:flex-col justify-between item-center my-4"
      >
        <div class="flex gap-2">
          <ix-date
            data-testid="notifications-from-date"
            .value=${this.fromDate}
            max=${this.maxDate}
            label="From"
            name="FromDate"
            .errorText=${this.fromDateErrorText}
            .onChanged=${(e: any) => this.onFromDateChange(e)}
          >
          </ix-date>
          <ix-date
            data-testid="notifications-to-date"
            .value=${this.toDate}
            max=${this.maxDate}
            label="To"
            name="ToDate"
            .errorText=${this.toDateErrorText}
            .onChanged=${(e: any) => this.onToDateChange(e)}
          ></ix-date>
        </div>
      </div>
      <div class="flex flex-row justify-between">
        <ix-button
          data-testid="notifications-cancel-date-filter"
          type=""
          target=""
          appearance="text"
          @click="${() => this.onApplyDateFilter(false)}"
          >Cancel</ix-button
        >
        <ix-button
          data-testid="notifications-activate-date-filter"
          type=""
          target=""
          appearance="text"
          @click="${() => this.onApplyDateFilter(true)}"
          >Filter List</ix-button
        >
      </div>
    </div>`;
  }
}
